import {createContext, ReactNode, useEffect, useRef, useState} from "react";
import {EVENT_TYPES} from "../utils/events.ts";

export type AlertType = 'error' | 'success' | 'info' | 'warning' | 'action';

export type Alert = {
  message: string,
  type: AlertType | AlertType[],
  id?: number,
  action?: {
    label: string,
    onClick: () => void
  }
}

type AlertsContextType = {
  alerts: Alert[],
  showAlert: (alert: Alert) => void,
  removeAlert: (id: number) => void
}

const contextDefaultValues: AlertsContextType = {
  alerts: [],
  showAlert: () => {},
  removeAlert: () => {}
}

const AlertsContext = createContext(contextDefaultValues);

export const DEFAULT_SNACKBAR_TIMEOUT = 5000;

const AlertsProvider = ({children}: {children: ReactNode}) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const alertsRef = useRef<Alert[]>([]);
  
  useEffect(() => {
    const handleNewAlert = (event: Event) => {
      showAlert((event as CustomEvent).detail);
    }
  
    window.addEventListener(EVENT_TYPES.NEW_ALERT, handleNewAlert);
    
    return () => { window.removeEventListener(EVENT_TYPES.NEW_ALERT, handleNewAlert); }
  }, []);

  function updateAlertsTransitioned() {
    document.startViewTransition ?
      document.startViewTransition(() => {
        setAlerts([...alertsRef.current]);
      }) :
      setAlerts([...alertsRef.current]);
  }
  
  function showAlert(alert: Alert) {
    // unique id - sometimes 2 alerts are created at the same time so we need to make sure they are unique for correct state handling
    alert.id = Date.now() + alertsRef.current.length;
    alertsRef.current.push(alert);
    updateAlertsTransitioned();
  }
  
  function removeAlert(id: number) {
    const alertToRemove = alertsRef.current.find((alert) => alert.id === id);
    if(!alertToRemove) return;
    alertsRef.current = alertsRef.current.filter((alert) => alert.id !== id);
    updateAlertsTransitioned();
  }

  return (
    <AlertsContext.Provider value={{alerts, showAlert, removeAlert}}>
      {children}
    </AlertsContext.Provider>
  );
}

export { AlertsContext, AlertsProvider };